import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Home, Wand2, HelpCircle, Image as ImageIcon, Menu, X, Search, CreditCard, Gauge, TrendingUp, Package, ChevronDown, ChevronRight, FileText, LayoutGrid, Route, Bot, Zap, ExternalLink, Sparkles, Quote, Table2 } from "lucide-react";
import DashboardActionFirst from "./DashboardActionFirst";
import AltTextGenerator from "./AltTextGenerator";
import Help from "./Help";
import ImageOptimizer from "./ImageOptimizer";
import SEOOptimizer from "./SEOOptimizer";
import PageSpeedInsights from "./PageSpeedInsights";
import LlmsTxt from "./LlmsTxt";
import IndexNow from "./IndexNow";
import Upgrade from "./Upgrade";
import BulkProductUpdate from "./BulkProductUpdate";
import MainImagePicker from "./MainImagePicker";
import AppGuidedTour from "./AppGuidedTour";
import TourReplayHintFlyout from "./TourReplayHintFlyout";
import HelpCopilotPanel from "./HelpCopilotPanel";
import {
  markGuidedTourCompleted,
  syncGuidedTourFromServer,
} from "../utils/guidedTourStorage";
import { getStoreId, getSettings } from "../utils/storage";
import { showToast } from "../utils/toast";
import { showQuotaExceededAlert } from "../utils/quotaAlert";
import * as logger from "../utils/logger";
import { openBulkEditProEcwidApp, openQuote2CartEcwidApp, openSearchIqEcwidApp } from "../utils/openEcwidApp";
import { UI_LANGUAGES, rememberUserLanguage } from "../i18n";
import "./MainApp.css";

const llmsTxtEnabled = (() => {
  const v = import.meta.env.VITE_LLMS_TXT_ENABLED;
  if (v === undefined || v === '') return true;
  const n = String(v).toLowerCase();
  return n === 'true' || n === '1';
})();

const indexNowEnabled = (() => {
  const v = import.meta.env.VITE_INDEXNOW_ENABLED;
  if (v === undefined || v === '') return true;
  const n = String(v).toLowerCase();
  return n === 'true' || n === '1';
})();

const MainApp = ({ storeInfo, onLogout }) => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [expandedMenuId, setExpandedMenuId] = useState(null); // 'seo-optimizer' | 'image-optimizer' | 'bulk-product-update' | 'alt-text-generator' | null
  const [seoOptimizerSubTab, setSeoOptimizerSubTab] = useState('overview');
  const [imageOptimizerSubTab, setImageOptimizerSubTab] = useState('images');
  const [altTextGeneratorSubTab, setAltTextGeneratorSubTab] = useState('products');
  const [bulkProductUpdateView, setBulkProductUpdateView] = useState('bulk-update');
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [seoOptimizerSidebarNonce, setSeoOptimizerSidebarNonce] = useState(0);
  const [guidedTourRun, setGuidedTourRun] = useState(false);
  const [dashboardRefreshTrigger, setDashboardRefreshTrigger] = useState(0);
  const [quotaExceededInfo, setQuotaExceededInfo] = useState(null);
  const [validationJobRunning, setValidationJobRunning] = useState(false);
  const [tourReplayFlyout, setTourReplayFlyout] = useState(false);
  const [pulseGuidedTourLink, setPulseGuidedTourLink] = useState(false);
  const [helpPanelOpen, setHelpPanelOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [hasAutoConnected, setHasAutoConnected] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Handle hash-based navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      const allowed = ['dashboard', 'alt-text-generator', 'help', 'image-optimizer', 'seo-optimizer', 'keyword-tracker', 'page-speed', 'bulk-product-update', 'upgrade', 'main-image-picker'];
      if (llmsTxtEnabled) allowed.push('llms-txt');
      if (indexNowEnabled) allowed.push('indexnow');
      if (!hash || !allowed.includes(hash)) return;
      if (!llmsTxtEnabled && hash === 'llms-txt') return;
      if (!indexNowEnabled && hash === 'indexnow') return;
      if (hash === 'keyword-tracker') {
        setActiveTab('seo-optimizer');
        setSeoOptimizerSubTab('keywordTracker');
      } else {
        setActiveTab(hash);
        if (hash === 'image-optimizer') setExpandedMenuId('image-optimizer');
        else if (hash === 'alt-text-generator') setExpandedMenuId('alt-text-generator');
        else if (hash === 'bulk-product-update') setExpandedMenuId('bulk-product-update');
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Auto-expand nested groups when entering a section (SEO uses flat top-level links)
  useEffect(() => {
    if (activeTab === 'image-optimizer') setExpandedMenuId('image-optimizer');
    else if (activeTab === 'alt-text-generator') setExpandedMenuId('alt-text-generator');
    else if (activeTab === 'bulk-product-update') setExpandedMenuId('bulk-product-update');
    else if (activeTab === 'main-image-picker') setExpandedMenuId('bulk-product-update');
  }, [activeTab]);

  // Handle window resize - close sidebar on mobile when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  // Ecwid iframe: single fixed height, no dynamic resize. Iframe is always this tall; content scrolls inside if needed.
  useEffect(() => {
    if (typeof window.EcwidApp === 'undefined' || window.self === window.top) return;
    const FIXED_IFRAME_HEIGHT = 1400;
    window.EcwidApp.setSize({ height: FIXED_IFRAME_HEIGHT });
  }, [activeTab]);

  // Load settings (skip fetch if already in storage from App's loadApiKeyAndSettings)
  useEffect(() => {
    const loadSettings = async () => {
      const storeId = getStoreId();
      logger.log('=== MainApp: loadSettings called ===');
      logger.log('MainApp: Store ID from getStoreId():', storeId);

      if (!storeId) {
        logger.log('MainApp: No store ID found in sessionStorage');
        setIsLoadingSettings(false);
        setSettings({});
        return;
      }

      const stored = getSettings();
      logger.log('MainApp: Settings from getSettings():', stored);
      if (stored && typeof stored === 'object' && Object.keys(stored).length > 0) {
        setSettings(stored);
        setIsLoadingSettings(false);
        return;
      }
      logger.log('MainApp: No stored settings found, fetching from backend...');
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      try {
        const response = await fetch(`${apiUrl}/api/settings?id=${storeId}`);
        logger.log('MainApp: Settings response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          logger.log('MainApp: Settings data received:', { 
            success: data.success, 
            hasSettings: !!data.settings,
            hasApiKey: !!data.settings?.apiKey,
            settingsKeys: data.settings ? Object.keys(data.settings) : []
          });
          
          if (data.success && data.settings) {
            setSettings(data.settings);
          } else {
            // Set empty object to indicate settings were loaded but no API key exists
            setSettings({});
          }
        } else {
          logger.warn('MainApp: Settings response not ok:', response.status);
          // Set empty object to indicate settings were attempted but failed
          setSettings({});
        }
      } catch (error) {
        logger.error('MainApp: Failed to load settings:', error);
        setSettings({});
      } finally {
        setIsLoadingSettings(false);
      }
    };
    
    loadSettings();
  }, []);

  // Auto-connect user when page loads without API key (similar to BigCommerce)
  useEffect(() => {
    logger.log('=== MainApp: Auto-connect check ===');
    logger.log('MainApp: Auto-connect conditions:', {
      isLoadingSettings,
      settings: settings ? 'exists' : 'null',
      hasApiKey: settings?.apiKey ? 'yes' : 'no',
      hasAutoConnected,
      isSaving
    });
    
    // Only auto-connect once, when settings are loaded and no API key exists
    // Match BigCommerce condition exactly: !isLoadingSettings && settings && !settings.apiKey && !hasAutoConnected && !isSaving
    // Note: settings can be {} (empty object) which is truthy, or null/undefined which is falsy
    if (!isLoadingSettings && settings && !settings.apiKey && !hasAutoConnected && !isSaving) {
      logger.log('MainApp: ✅ Auto-connect condition met, starting auto-connect...');
      setHasAutoConnected(true);
      const storeId = getStoreId(); // Use utility function
      logger.log('MainApp: Store ID for auto-connect:', storeId);
      
      if (!storeId) {
        logger.error('MainApp: ❌ No store ID found for auto-connect');
        setHasAutoConnected(false);
        return;
      }
      
      // Use setTimeout to avoid calling during render (matching BigCommerce)
      setTimeout(() => {
        const apiUrl = import.meta.env.VITE_BACKEND_URL;
        const registerUrl = `${apiUrl}/api/register?id=${storeId}`;
        logger.log('MainApp: 📞 Calling register endpoint:', registerUrl);
        
        fetch(registerUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        })
        .then(res => {
          logger.log('MainApp: Register response status:', res.status);
          if (!res.ok) {
            return res.json().then(err => {
              throw new Error(err.error || err.message || `HTTP ${res.status}`);
            });
          }
          return res.json();
        })
        .then(data => {
          logger.log('MainApp: ✅ Auto-connect response:', data);
          if (data.success || data.api_key) {
            // Reload the page to get fresh settings with the new API key
            logger.log('MainApp: ✅ Auto-connect successful, reloading page...');
            window.location.reload();
          } else {
            logger.warn('MainApp: ⚠️ Auto-connect response missing success/api_key:', data);
            setHasAutoConnected(false); // Reset on failure
          }
        })
        .catch(err => {
          logger.error('MainApp: ❌ Auto-connect failed:', err);
          setHasAutoConnected(false); // Reset on error
        });
      }, 100);
    } else {
      logger.log('MainApp: ⏸️ Auto-connect skipped - conditions not met');
    }
  }, [settings, isLoadingSettings, hasAutoConnected, isSaving]);

  // Reset hasAutoConnected once API key is successfully loaded
  useEffect(() => {
    if (hasAutoConnected && settings?.apiKey) {
      logger.log('MainApp: API key loaded, resetting hasAutoConnected');
      setHasAutoConnected(false);
    }
  }, [hasAutoConnected, settings?.apiKey]);

  const closeMobileNav = useCallback(() => {
    if (window.innerWidth < 768) setSidebarOpen(false);
  }, []);

  const handleTabClick = useCallback(
    (tab) => {
      setActiveTab(tab);
      window.location.hash = tab;
      closeMobileNav();
    },
    [closeMobileNav]
  );

  const beginGuidedTour = useCallback(() => {
    setHelpPanelOpen(false);
    setActiveTab('dashboard');
    window.location.hash = 'dashboard';
    closeMobileNav();
    window.setTimeout(() => setGuidedTourRun(true), 500);
  }, [closeMobileNav]);

  const handleGuidedTourClose = useCallback(() => {
    setGuidedTourRun(false);
  }, []);

  const handleGuidedTourExit = useCallback(() => {
    setTourReplayFlyout(true);
  }, []);

  const handleTourReplayFlyoutFinished = useCallback(() => {
    setTourReplayFlyout(false);
    setPulseGuidedTourLink(true);
    window.setTimeout(() => setPulseGuidedTourLink(false), 2200);
  }, []);

  const handleHelpGoTo = useCallback(
    (section) => {
      switch (section) {
        case 'dashboard':
          handleTabClick('dashboard');
          break;
        case 'seo-optimizer':
          setSeoOptimizerSubTab('overview');
          handleTabClick('seo-optimizer');
          break;
        case 'seo-bulk':
          setSeoOptimizerSubTab('automation');
          setSeoOptimizerSidebarNonce((n) => n + 1);
          handleTabClick('seo-optimizer');
          break;
        case 'seo-keywords':
          setSeoOptimizerSubTab('keywordTracker');
          setSeoOptimizerSidebarNonce((n) => n + 1);
          handleTabClick('seo-optimizer');
          break;
        case 'alt-text-products':
          setAltTextGeneratorSubTab('products');
          handleTabClick('alt-text-generator');
          break;
        case 'image-optimizer':
          setImageOptimizerSubTab('images');
          handleTabClick('image-optimizer');
          break;
        case 'bulk-product-editor':
          setBulkProductUpdateView('bulk-update');
          handleTabClick('bulk-product-update');
          break;
        case 'pricing-new':
          handleTabClick('upgrade');
          break;
        case 'main-image-picker':
          handleTabClick('main-image-picker');
          break;
        case 'help':
          handleTabClick('help');
          break;
        default:
          break;
      }
    },
    [handleTabClick]
  );

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const showContentLoading = isLoadingSettings || (hasAutoConnected && !settings?.apiKey);

  useEffect(() => {
    const storeId = getStoreId();
    if (!storeId || isLoadingSettings) return;
    if (settings?.guidedTourCompleted === true) {
      markGuidedTourCompleted();
    } else {
      void syncGuidedTourFromServer(storeId);
    }
  }, [isLoadingSettings, settings?.guidedTourCompleted]);

  const handleSeoQuickAction = useCallback(async (action) => {
    const storeId = getStoreId();
    if (!storeId) return;

    if (action !== 'validate-all' && action !== 'refresh-outdated') return;

    if (quotaExceededInfo) {
      await showQuotaExceededAlert(
        t('dashboard.quotaExceededTitle'),
        t('dashboard.quotaExceededToast', {
          used: quotaExceededInfo.used,
          limit: quotaExceededInfo.limit,
          plan: quotaExceededInfo.accountType,
        }),
        t('common.ok')
      );
      return;
    }

    setValidationJobRunning(true);
    const apiUrl = import.meta.env.VITE_BACKEND_URL;
    try {
      const scope = action === 'refresh-outdated' ? 'outdated' : 'all';
      const response = await fetch(`${apiUrl}/api/seo-optimizer/validate?id=${storeId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.status === 403 && (data.error === 'Quota exceeded' || data.data)) {
        const q = data.data || {};
        setQuotaExceededInfo({
          used: q.used ?? quotaExceededInfo?.used,
          limit: q.limit ?? quotaExceededInfo?.limit,
          accountType: q.accountType || 'trial',
        });
        await showQuotaExceededAlert(
          t('dashboard.quotaExceededTitle'),
          data.error || t('dashboard.quotaExceededUpgradeToast'),
          t('common.ok')
        );
        return;
      }
      if (response.status === 409 && (data.code === 'VALIDATION_IN_PROGRESS' || data.error)) {
        showToast(data.error || t('dashboard.validationInProgress'), 'info', 6000);
        return;
      }
      if (!response.ok) {
        showToast(data.error || t('dashboard.failedToStartValidation'), 'error');
        return;
      }
      showToast(data.message || t('dashboard.validationStarted'), 'success');
      setTimeout(() => setDashboardRefreshTrigger((n) => n + 1), 2000);
    } catch (err) {
      logger.error('Dashboard quick action failed:', err);
      showToast(err.message || t('dashboard.failedToStartValidation'), 'error');
    } finally {
      setValidationJobRunning(false);
    }
  }, [quotaExceededInfo, t]);

  const handleDashboardNavigate = useCallback(
    ({ tab, subTab, quickAction }) => {
      if (quickAction) {
        void handleSeoQuickAction(quickAction);
        return;
      }
      if (tab === 'seo-bulk') {
        setSeoOptimizerSubTab('automation');
        setSeoOptimizerSidebarNonce((n) => n + 1);
        setActiveTab('seo-optimizer');
        window.location.hash = 'seo-optimizer';
        closeMobileNav();
        return;
      }
      if (tab === 'seo-keywords') {
        setSeoOptimizerSubTab('keywordTracker');
        setSeoOptimizerSidebarNonce((n) => n + 1);
        setActiveTab('seo-optimizer');
        window.location.hash = 'seo-optimizer';
        closeMobileNav();
        return;
      }
      setActiveTab(tab);
      window.location.hash = tab;
      if (subTab) {
        setSeoOptimizerSubTab(subTab);
        if (tab === 'seo-optimizer') {
          setSeoOptimizerSidebarNonce((n) => n + 1);
        }
      }
      closeMobileNav();
    },
    [handleSeoQuickAction, closeMobileNav]
  );

  useEffect(() => {
    if (guidedTourRun && activeTab !== 'dashboard') {
      setGuidedTourRun(false);
    }
  }, [activeTab, guidedTourRun]);

  const renderTabContent = () => {
    if (showContentLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="text-center">
            <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
              <span className="visually-hidden">{t("mainApp.loading")}</span>
            </div>
            <p className="text-muted small mb-0">
              {hasAutoConnected ? t("mainApp.connectingAccount") : t("mainApp.loadingSettings")}
            </p>
          </div>
        </div>
      );
    }
    switch (activeTab) {
      case 'dashboard':
        return (
          <DashboardActionFirst
            storeId={getStoreId()}
            onNavigate={handleDashboardNavigate}
            validationDisabled={validationJobRunning}
            quotaExceeded={quotaExceededInfo}
            refreshTrigger={dashboardRefreshTrigger}
            onQuotaExceeded={setQuotaExceededInfo}
            onFixErrors={() => {
              setSeoOptimizerSubTab('overview');
              setActiveTab('seo-optimizer');
              window.location.hash = 'seo-optimizer';
            }}
            onQuickAction={handleSeoQuickAction}
          />
        );
      case 'alt-text-generator':
        return <AltTextGenerator initialTab={altTextGeneratorSubTab} />;
      case 'seo-optimizer':
        return (
          <SEOOptimizer
            initialTab={seoOptimizerSubTab}
            sidebarNavNonce={seoOptimizerSidebarNonce}
            onOpenPricing={() => handleTabClick('upgrade')}
          />
        );
      case 'image-optimizer':
        return <ImageOptimizer initialTab={imageOptimizerSubTab} />;
      case 'page-speed':
        return <PageSpeedInsights storeInfo={storeInfo} />;
      case 'llms-txt':
        return llmsTxtEnabled ? (
          <LlmsTxt onOpenPricing={() => handleTabClick('upgrade')} />
        ) : null;
      case 'indexnow':
        return indexNowEnabled ? (
          <IndexNow onOpenPricing={() => handleTabClick('upgrade')} />
        ) : null;
      case 'main-image-picker':
        return <MainImagePicker />;
      case 'bulk-product-update':
        return (
          <BulkProductUpdate
            initialView={bulkProductUpdateView}
            onOpenMainImagePicker={() => handleTabClick('main-image-picker')}
          />
        );
      case 'upgrade':
        return <Upgrade />;
      case 'help':
        return <Help />;
      default:
        return (
          <DashboardActionFirst
            storeId={getStoreId()}
            onNavigate={handleDashboardNavigate}
            validationDisabled={validationJobRunning}
            quotaExceeded={quotaExceededInfo}
            refreshTrigger={dashboardRefreshTrigger}
            onQuotaExceeded={setQuotaExceededInfo}
            onFixErrors={() => {
              setSeoOptimizerSubTab('overview');
              setActiveTab('seo-optimizer');
              window.location.hash = 'seo-optimizer';
            }}
            onQuickAction={handleSeoQuickAction}
          />
        );
    }
  };

  return (
      <div className="main-app h-100 overflow-hidden" data-main-layout="true" data-active-tab={activeTab}>
      <div className="container-fluid p-0 h-100 d-flex flex-column overflow-hidden" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="row g-0 flex-grow-1 m-0 h-100 overflow-hidden">
          {/* Mobile Hamburger Button */}
          <button
            className="mobile-menu-toggle"
            onClick={toggleSidebar}
            aria-label={t("mainApp.toggleMenu")}
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Mobile Overlay */}
          {sidebarOpen && (
            <div 
              className="sidebar-overlay"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Left Sidebar Menu */}
          <div 
            className={`col-md-3 col-lg-2 sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}
          >
            {/* Logo and Store Info */}
            <div className="sidebar-header">
              <img 
                src="/img/seospider-long.webp"
                alt={t("mainApp.logoAlt")}
                className="sidebar-logo"
              />
              {storeInfo && (
                <p className="sidebar-store-name">{storeInfo.name}</p>
              )}
            </div>

            {/* Navigation Menu */}
            <nav className="sidebar-nav">
              <button
                id="tour-nav-dashboard"
                type="button"
                className={`sidebar-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => handleTabClick('dashboard')}
              >
                <Home size={16} className="me-2" />
                {t("mainApp.nav.dashboard")}
              </button>
              <button
                id="tour-nav-seo"
                type="button"
                className={`sidebar-nav-item ${
                  activeTab === 'seo-optimizer' && seoOptimizerSubTab === 'overview' ? 'active' : ''
                }`}
                onClick={() => {
                  setSeoOptimizerSubTab('overview');
                  handleTabClick('seo-optimizer');
                }}
              >
                <Search size={16} className="me-2" />
                {t('mainApp.nav.aiSeoOptimizer')}
              </button>
              <button
                id="tour-nav-seo-bulk"
                type="button"
                className={`sidebar-nav-item ${
                  activeTab === 'seo-optimizer' && seoOptimizerSubTab === 'automation' ? 'active' : ''
                }`}
                onClick={() => {
                  setSeoOptimizerSubTab('automation');
                  setSeoOptimizerSidebarNonce((n) => n + 1);
                  handleTabClick('seo-optimizer');
                }}
              >
                <FileText size={16} className="me-2" />
                {t('mainApp.nav.seoBulkOptimizer')}
              </button>
              <button
                id="tour-nav-keyword-tracker"
                type="button"
                className={`sidebar-nav-item ${
                  activeTab === 'seo-optimizer' && seoOptimizerSubTab === 'keywordTracker' ? 'active' : ''
                }`}
                onClick={() => {
                  setSeoOptimizerSubTab('keywordTracker');
                  setSeoOptimizerSidebarNonce((n) => n + 1);
                  handleTabClick('seo-optimizer');
                }}
              >
                <TrendingUp size={16} className="me-2" />
                {t('mainApp.nav.keywordTracker')}
              </button>
              {/* Image Optimizer - nested menu */}
              <div className="sidebar-nav-group">
                <button
                  id="tour-nav-image-opt"
                  type="button"
                  className="sidebar-nav-item sidebar-nav-item-parent"
                  onClick={() => {
                    if (activeTab !== 'image-optimizer') {
                      handleTabClick('image-optimizer');
                      setImageOptimizerSubTab('images');
                      setExpandedMenuId('image-optimizer');
                    } else {
                      setExpandedMenuId((prev) => (prev === 'image-optimizer' ? null : 'image-optimizer'));
                    }
                  }}
                >
                  <ImageIcon size={16} className="me-2" />
                  <span className="flex-grow-1 text-start">{t("mainApp.nav.imageOptimizer")}</span>
                  <span className="sidebar-nav-chevron">{expandedMenuId === 'image-optimizer' ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
                </button>
                {expandedMenuId === 'image-optimizer' && (
                  <div className="sidebar-nav-children">
                    {['images', 'settings', 'history'].map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        className={`sidebar-nav-subitem ${activeTab === 'image-optimizer' && imageOptimizerSubTab === tab ? 'active' : ''}`}
                        onClick={() => {
                          setActiveTab('image-optimizer');
                          setImageOptimizerSubTab(tab);
                          window.location.hash = 'image-optimizer';
                        }}
                      >
                        {tab === 'images' && t("mainApp.nav.images")}
                        {tab === 'settings' && t("mainApp.nav.settings")}
                        {tab === 'history' && t("mainApp.nav.history")}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Alt Text Generator - nested menu */}
              <div className="sidebar-nav-group">
                <button
                  id="tour-nav-alt-text"
                  type="button"
                  className="sidebar-nav-item sidebar-nav-item-parent"
                  onClick={() => {
                    if (activeTab !== 'alt-text-generator') {
                      handleTabClick('alt-text-generator');
                      setAltTextGeneratorSubTab('products');
                      setExpandedMenuId('alt-text-generator');
                    } else {
                      setExpandedMenuId((prev) => (prev === 'alt-text-generator' ? null : 'alt-text-generator'));
                    }
                  }}
                >
                  <Wand2 size={16} className="me-2" />
                  <span className="flex-grow-1 text-start">{t("mainApp.nav.altTextGenerator")}</span>
                  <span className="sidebar-nav-chevron">{expandedMenuId === 'alt-text-generator' ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
                </button>
                {expandedMenuId === 'alt-text-generator' && (
                  <div className="sidebar-nav-children">
                    {['products', 'history', 'settings'].map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        className={`sidebar-nav-subitem ${activeTab === 'alt-text-generator' && altTextGeneratorSubTab === tab ? 'active' : ''}`}
                        onClick={() => {
                          setActiveTab('alt-text-generator');
                          setAltTextGeneratorSubTab(tab);
                          window.location.hash = 'alt-text-generator';
                        }}
                      >
                        {tab === 'products' && t('altTextGenerator.generateAltTextTab')}
                        {tab === 'history' && t("mainApp.nav.history")}
                        {tab === 'settings' && t("mainApp.nav.settings")}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Bulk Product Update - nested menu */}
              <div className="sidebar-nav-group">
                <button
                  id="tour-nav-bulk-product-editor"
                  type="button"
                  className="sidebar-nav-item sidebar-nav-item-parent"
                  onClick={() => {
                    if (activeTab !== 'bulk-product-update') {
                      handleTabClick('bulk-product-update');
                      setBulkProductUpdateView('bulk-update');
                      setExpandedMenuId('bulk-product-update');
                    } else {
                      setExpandedMenuId((prev) => (prev === 'bulk-product-update' ? null : 'bulk-product-update'));
                    }
                  }}
                >
                  <Package size={16} className="me-2" />
                  <span className="flex-grow-1 text-start">{t("mainApp.nav.bulkProductUpdate")}</span>
                  <span className="sidebar-nav-chevron">{expandedMenuId === 'bulk-product-update' ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
                </button>
                {expandedMenuId === 'bulk-product-update' && (
                  <div className="sidebar-nav-children">
                    <button
                      type="button"
                      className={`sidebar-nav-subitem ${activeTab === 'bulk-product-update' && bulkProductUpdateView === 'bulk-update' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab('bulk-product-update');
                        setBulkProductUpdateView('bulk-update');
                        window.location.hash = 'bulk-product-update';
                      }}
                    >
                      {t("mainApp.nav.bulkUpdate")}
                    </button>
                    <button
                      type="button"
                      className={`sidebar-nav-subitem ${activeTab === 'bulk-product-update' && bulkProductUpdateView === 'rollback' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab('bulk-product-update');
                        setBulkProductUpdateView('rollback');
                        window.location.hash = 'bulk-product-update';
                      }}
                    >
                      {t("mainApp.nav.history")}
                    </button>
                    <button
                      type="button"
                      className={`sidebar-nav-subitem ${activeTab === 'main-image-picker' ? 'active' : ''}`}
                      onClick={() => handleTabClick('main-image-picker')}
                    >
                      {t("bulkProductUpdate.linkMainImage")}
                    </button>
                  </div>
                )}
              </div>   
              <button
                id="tour-nav-page-speed"
                type="button"
                className={`sidebar-nav-item ${activeTab === 'page-speed' ? 'active' : ''}`}
                onClick={() => handleTabClick('page-speed')}
              >
                <Gauge size={16} className="me-2" />
                {t("mainApp.nav.pageSpeed")}
              </button>
              {llmsTxtEnabled && (
                <button
                  id="tour-nav-llms-txt"
                  type="button"
                  className={`sidebar-nav-item ${activeTab === 'llms-txt' ? 'active' : ''}`}
                  onClick={() => handleTabClick('llms-txt')}
                >
                  <Bot size={16} className="me-2" />
                  <span className="flex-grow-1 text-start">{t("mainApp.nav.llmsTxt")}</span>
                  <span className="badge rounded-pill ms-1" style={{ fontSize: '0.65rem', background: activeTab === 'llms-txt' ? 'rgba(255,255,255,0.25)' : '#0d6efd', color: '#fff' }}>
                    New
                  </span>
                </button>
              )}
              {indexNowEnabled && (
                <button
                  id="tour-nav-indexnow"
                  type="button"
                  className={`sidebar-nav-item ${activeTab === 'indexnow' ? 'active' : ''}`}
                  onClick={() => handleTabClick('indexnow')}
                >
                  <Zap size={16} className="me-2" />
                  <span className="flex-grow-1 text-start">IndexNow</span>
                  <span className="badge rounded-pill ms-1" style={{ fontSize: '0.65rem', background: activeTab === 'indexnow' ? 'rgba(255,255,255,0.25)' : '#ea580c', color: '#fff' }}>
                    New
                  </span>
                </button>
              )}
              <button
                id="tour-nav-upgrade"
                type="button"
                className={`sidebar-nav-item ${activeTab === 'upgrade' ? 'active' : ''}`}
                onClick={() => handleTabClick('upgrade')}
              >
                <CreditCard size={16} className="me-2" />
                {t("mainApp.nav.upgrade")}
              </button>
              <button
                id="tour-nav-help"
                type="button"
                className={`sidebar-nav-item ${activeTab === 'help' ? 'active' : ''}`}
                onClick={() => handleTabClick('help')}
              >
                <HelpCircle size={16} className="me-2" />
                {t("mainApp.nav.help")}
              </button>

              <div className="sidebar-other-apps">
                <div className="sidebar-other-apps-label">
                  <Sparkles size={11} className="sidebar-other-apps-label-icon" aria-hidden />
                  {t('mainApp.otherApps')}
                </div>
                <div className="sidebar-other-apps-list">
                  <button
                    type="button"
                    className="sidebar-other-apps-card"
                    onClick={() => openSearchIqEcwidApp()}
                    title={t('mainApp.openSearchIq')}
                  >
                    <span className="sidebar-other-apps-icon" aria-hidden>
                      <Search size={15} strokeWidth={2.25} />
                    </span>
                    <span className="sidebar-other-apps-copy">
                      <span className="sidebar-other-apps-title">{t('mainApp.nav.searchIq')}</span>
                      <span className="sidebar-other-apps-desc">{t('mainApp.searchIqTagline')}</span>
                    </span>
                    <ExternalLink size={13} className="sidebar-other-apps-external" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="sidebar-other-apps-card"
                    onClick={() => openQuote2CartEcwidApp()}
                    title={t('mainApp.openQuote2Cart')}
                  >
                    <span className="sidebar-other-apps-icon" aria-hidden>
                      <Quote size={15} strokeWidth={2.25} />
                    </span>
                    <span className="sidebar-other-apps-copy">
                      <span className="sidebar-other-apps-title">{t('mainApp.nav.quote2Cart')}</span>
                      <span className="sidebar-other-apps-desc">{t('mainApp.quote2CartTagline')}</span>
                    </span>
                    <ExternalLink size={13} className="sidebar-other-apps-external" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="sidebar-other-apps-card"
                    onClick={() => openBulkEditProEcwidApp()}
                    title={t('mainApp.openBulkEditPro')}
                  >
                    <span className="sidebar-other-apps-icon" aria-hidden>
                      <Table2 size={15} strokeWidth={2.25} />
                    </span>
                    <span className="sidebar-other-apps-copy">
                      <span className="sidebar-other-apps-title">{t('mainApp.nav.bulkEditPro')}</span>
                      <span className="sidebar-other-apps-desc">{t('mainApp.bulkEditProTagline')}</span>
                    </span>
                    <ExternalLink size={13} className="sidebar-other-apps-external" aria-hidden />
                  </button>
                </div>
              </div>

              <div className="px-2 pt-2 pb-1 mt-2 border-top" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                <div className="small text-muted text-uppercase mb-1" style={{ fontSize: '0.65rem', letterSpacing: '0.06em' }}>
                  {t('mainApp.learnTheApp')}
                </div>
                <div className="d-flex flex-column gap-1">
                  <button
                    id="sidebar-guided-tour-link"
                    type="button"
                    className="btn btn-link text-start p-0 small text-decoration-none d-flex align-items-center gap-1"
                    style={{
                      fontSize: '0.82rem',
                      color: '#0d6efd',
                      borderRadius: 6,
                      boxShadow: pulseGuidedTourLink ? '0 0 0 3px rgba(13, 110, 253, 0.45)' : undefined,
                      transition: 'box-shadow 0.35s ease',
                    }}
                    onClick={() => beginGuidedTour()}
                  >
                    <Route size={14} className="flex-shrink-0" aria-hidden />
                    {t('mainApp.guidedTour')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-link text-start p-0 small text-decoration-none d-flex align-items-center gap-1"
                    style={{ fontSize: '0.82rem', color: '#0d6efd' }}
                    onClick={() => setHelpPanelOpen(true)}
                  >
                    <LayoutGrid size={14} />
                    {t('mainApp.applicationMap')}
                  </button>
                </div>
              </div>
            </nav>

            <div className="sidebar-language-card">
              <label className="sidebar-language-card-header" htmlFor="sidebar-language">
                {t("mainApp.language")}
              </label>
              <div className="sidebar-language-card-body">
                <select
                  id="sidebar-language"
                  className="sidebar-language-select"
                  value={UI_LANGUAGES.some((lang) => lang.code === i18n.resolvedLanguage) ? i18n.resolvedLanguage : "en"}
                  onChange={(e) => rememberUserLanguage(e.target.value)}
                >
                  {UI_LANGUAGES.map(({ code }) => (
                    <option key={code} value={code}>
                      {t(`mainApp.langNames.${code}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Main Content Area - wrapper scrolls when content exceeds viewport */}
          <div className="col-md-9 col-lg-10 h-100 d-flex flex-column p-0">
            <div className="main-content-wrapper flex-grow-1">
            <main className="main-content">
              {renderTabContent()}
            </main>
            </div>
          </div>
        </div>
      </div>

      <AppGuidedTour
        run={guidedTourRun}
        onClose={handleGuidedTourClose}
        onExit={handleGuidedTourExit}
        storeId={getStoreId()}
      />
      {tourReplayFlyout && <TourReplayHintFlyout onFinished={handleTourReplayFlyoutFinished} />}
      <HelpCopilotPanel
        isOpen={helpPanelOpen}
        onClose={() => setHelpPanelOpen(false)}
        onGoTo={handleHelpGoTo}
        onStartGuidedTour={beginGuidedTour}
      />
    </div>
  );
};

export default MainApp; 